<template>
  <div class="add-to-cart">
    <form @submit.prevent="submit">
      <div class="purchase-options" role="group">
        <template v-for="purchaseOption of purchaseOptions">
          <div
            v-for="selectedVariant of [
              selected[purchaseOption.type]?.variant
            ]?.filter(Boolean)"
            :key="selectedVariant.id"
            :aria-labelledby="`label-${purchaseOption.id}`"
            :class="{
              'purchase-option--selected':
                selectedPurchaseOptionType === purchaseOption.type
            }"
            class="purchase-option"
            role="group"
          >
            <div class="purchase-option__radio">
              <input
                :id="`input-${purchaseOption.id}`"
                :checked="selectedPurchaseOptionType === purchaseOption.type"
                :value="purchaseOption.type"
                :data-testid="`purchase-option-${purchaseOption.type}`"
                class="purchase-option__radio-input"
                name="purchaseOption"
                type="radio"
                @change="selectedPurchaseOptionType = purchaseOption.type"
              />
              <svg
                aria-hidden="true"
                class="purchase-option__radio-icon"
                height="20"
                width="20"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10s4.477 10 10 10 10-4.477 10-10ZM8.6 17.1l8.5-11.9-2.7-1.9L8 12.4l-3-2.2-2 2.6 5.6 4.3Z"
                  fill="currentcolor"
                />
                <ellipse
                  cx="10"
                  cy="10"
                  rx="11"
                  ry="11"
                  fill="none"
                  stroke="currentcolor"
                  stroke-width="2.5"
                  vector-effect="nonScalingStroke"
                />
              </svg>
            </div>
            <label
              :id="`label-${purchaseOption.id}`"
              :for="`input-${purchaseOption.id}`"
              class="purchase-option__label"
            >
              {{ purchaseOption.subtitle || selectedVariant.title }}
              <span v-if="purchaseOption.title">{{
                purchaseOption.title
              }}</span>
            </label>
            <div
              v-if="
                /* Hide the price on the gift card PDP */
                purchaseOption.product.title !== 'Recess Gift Card'
              "
              class="purchase-option__price"
            >
              <strong>
                {{ formatPrice(selectedVariant.price) }}
              </strong>
              <s v-if="selectedVariant.compareAtPrice">
                {{ formatPrice(selectedVariant.compareAtPrice) }}
              </s>
            </div>
            <span v-if="purchaseOption.badge" class="purchase-option__badge">
              {{ purchaseOption.badge }}
            </span>
            <ul v-if="purchaseOption.details" class="purchase-option__details">
              <li v-for="detail of purchaseOption.details" :key="detail">
                {{ detail }}
              </li>
            </ul>
            <expand-height>
              <template
                v-if="selectedPurchaseOptionType === purchaseOption.type"
              >
                <select-dropdown
                  v-if="purchaseOption.sellingPlans"
                  :options="purchaseOption.sellingPlans"
                  :value="selected[purchaseOption.type].sellingPlan?.value"
                  label="Subscription frequency"
                  visually-hidden-label
                  @input="
                    value => setSelectedSellingPlan(purchaseOption.type, value)
                  "
                />
                <select-dropdown
                  v-for="productOption of purchaseOption.options"
                  :key="productOption.id"
                  :value="
                    selectedVariant.selectedOptions.find(
                      o => o.name === productOption.name
                    )?.value
                  "
                  :options="
                    productOption.values.map(value => ({ key: value, value }))
                  "
                  :label="productOption.name"
                  visually-hidden-label
                  @input="
                    value =>
                      setSelectedVariantForOption(purchaseOption.type, {
                        name: productOption.name,
                        value
                      })
                  "
                />
              </template>
            </expand-height>
          </div>
        </template>
      </div>
      <ul v-if="purchaseSettings?.details" class="purchase-details">
        <li v-for="detail of purchaseSettings.details" :key="detail">
          {{ detail }}
        </li>
      </ul>
      <number-input
        v-model="model.quantity"
        :min="1"
        :max="maxQuantity"
        :disabled="soldOut"
        class="quantity-input"
      />
      <cta
        class="text-lg whitespace-no-wrap flex-grow"
        :disabled="isSubmitting"
        fill
        @click.prevent="submit"
      >
        Add to Cart
      </cta>
    </form>
    <div
      v-if="hasActivePrimeWidget || amazonCta"
      class="separator"
      role="separator"
    >
      or
    </div>
    <a
      v-if="!hasActivePrimeWidget && amazonCta"
      class="amazon-cta"
      :href="amazonCta.url"
      @click="trackAmazonAddToCart"
    >
      <span class="amazon-cta__text">
        {{ amazonCta.text }}
      </span>
      <img
        v-if="amazonCta.image"
        alt="Amazon logo"
        class="amazon-cta__image"
        :src="amazonCta.image"
        height="30"
      />
    </a>
    <buy-with-prime
      v-if="'single' === selectedPurchaseOptionType"
      :sku="selected.single?.variant?.sku"
      @widgetloaded="hasActivePrimeWidget = true"
      @widgetdestroyed="hasActivePrimeWidget = false"
    />
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { mapState, mapGetters, mapActions } from 'vuex'
import formatLineItemSchema from '~/util/shopify/format-line-item-schema'
import formatLineItemAttributes from '~/util/shopify/format-line-item-attributes'
import formatPrice from '~/util/format-price'
import NumberInput from '~/components/form/number-input.vue'
import BuyWithPrime from '~/components/shop/buy-with-prime.vue'
import SelectDropdown from '~/components/form/select-dropdown.vue'

export default {
  name: 'AddProductToCart',

  components: {
    NumberInput,
    SelectDropdown,
    BuyWithPrime
  },

  props: {
    purchaseSettings: {
      default: () => ({}),
      type: Object
    },
    purchaseOptions: {
      default: () => [],
      type: Array
    }
  },

  data() {
    return {
      model: { quantity: 1 },
      isSubmitting: false,
      hasActivePrimeWidget: false,
      selectedPurchaseOptionType: null,
      selected: {}
    }
  },

  computed: {
    ...mapState({
      googleClientId: state => state.googleClientId,
      cartId: state => state.storage.cartId,
      amazonSettings: state => state.settings.amazonSettings,
      rebuyTracker: state => state.shop.rebuyTracker
    }),
    ...mapGetters({
      bp: 'screen/breakpoint',
      productsByCategory: 'shop/productsByCategory'
    }),
    amazonCta() {
      const type = this.selectedPurchaseOptionType
      const selected = this.purchaseOptions.find(o => o.type === type)
      const defaultCTA =
        (!selected.containsCBD && this.amazonSettings?.cta) || undefined
      const productCTA = this.purchaseSettings?.amazon?.productLink
      const url = productCTA?.url || defaultCTA?.url
      const text = productCTA?.text || defaultCTA?.text
      const image = defaultCTA?.image
      return text && url ? { image, text, url } : undefined
    },
    /**
     * Returns a subscription variant with sku matching
     * the product's sku (if one exists)
     * @function
     */
    soldOut() {
      const type = this.selectedPurchaseOptionType
      const selectedVariant = this.selected[type]?.variant
      return !selectedVariant?.availableForSale
    },
    maxQuantity() {
      const type = this.selectedPurchaseOptionType
      const selectedVariant = this.selected[type]?.variant
      if (!selectedVariant) {
        return undefined
      }
      if (!selectedVariant.availableForSale) {
        return 0
      }
      if (selectedVariant.quantityAvailable <= 0) {
        return 9999
      }
      return selectedVariant.quantityAvailable
    }
  },

  watch: {
    'model.quantity': {
      handler(newValue) {
        if (newValue === undefined || newValue === null) {
          this.model.quantity = 1
        }
      },
      immediate: true
    },
    selected(next, prev) {
      const type = this.selectedPurchaseOptionType
      if (
        next[type]?.variant &&
        next[type].variant.id !== prev[type]?.variant?.id
      ) {
        const { product } = this.purchaseOptions.find(o => o.type === type)
        const { variant } = next[type]
        this.$gtm.push({
          event: 'Product Viewed',
          google_client_id: this.googleClientId,
          product_id: product.id,
          sku: variant.sku,
          name: product?.title,
          variant: variant?.title,
          price: variant.price.amount,
          quantity: this.model.quantity,
          value: variant.price.amount,
          url: `${process.env.canonicalUrl}${this.$route.path}`
        })
      }
    }
  },

  created() {
    this.debouncedSubmit = debounce(
      this.submit,
      process.env.shopifyRequestDebounce
    )
    // clear rebuy tracker on component cleanup
    this.$on('hook:beforeDestroy', () => {
      if (this.purchaseOptions.some(o => o.product.id === this.rebuyTracker)) {
        this.$store.dispatch('shop/clearRebuyTracker')
      }
    })
  },

  mounted() {
    // Set the default selections
    this.selectedPurchaseOptionType = this.purchaseOptions[0]?.type
    this.selected = this.purchaseOptions.reduce(
      (acc, option) => ({
        ...acc,
        [option.type]: {
          variant: option.defaultVariant,
          sellingPlan: option.sellingPlans?.[0]
        }
      }),
      {}
    )
  },

  methods: {
    ...mapActions({
      addToCart: 'shop/addToCart'
    }),
    formatPrice(price) {
      return formatPrice(price)
    },
    /**
     * Formats and adds the current product or subscription
     * variant to the user's checkout as a line item based on
     * the current product type
     * @function
     */
    async submit() {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true

      const purchaseType = this.selectedPurchaseOptionType
      const { category, id, product } = this.purchaseOptions.find(
        o => o.type === purchaseType
      )
      const { sellingPlan, variant } = this.selected[purchaseType]

      let quantity = this.model.quantity

      if (!variant.availableForSale) {
        quantity = 0
      } else if (
        variant.quantityAvailable > 0 &&
        variant.quantityAvailable < quantity
      ) {
        quantity = variant.quantityAvailable
      }

      const attributes = formatLineItemAttributes({
        isRebuyProductDiscovery: this.rebuyTracker === product.id
      })

      // Some products, esp. subscription products, may not have a specified "product type"
      const productType =
        product.productType ||
        this.purchaseOptions.find(o => o.product.productType)?.productType ||
        null

      const line = formatLineItemSchema(
        { ...product, attributes, productType, quantity, sellingPlan, variant },
        'product'
      )

      await this.addToCart({ edge: line, additive: true })

      this.trackAdded({
        cart: this.cartId,
        id: product.id,
        decodedId: id,
        category,
        sku: variant.sku,
        name: product.title,
        variant: variant.title,
        price: variant.price.amount,
        quantity,
        url: `${process.env.canonicalUrl}${this.$route.path}`,
        image: variant.image?.transformedSrc,
        discount: sellingPlan?.price || undefined
      })

      this.isSubmitting = false
    },
    trackAmazonAddToCart(e) {
      try {
        const purchaseType = this.selectedPurchaseOptionType
        const variant = this.selected[purchaseType]?.variant
        const { id, product } = this.purchaseOptions.find(
          o => o.type === 'single'
        )

        this.$gtm.push({
          event: 'Add To Cart',
          google_client_id: this.googleClientId,
          cart_id: this.cartId,
          product_id: product.id,
          product_id_shopify: id,
          sku: variant.sku,
          name: product.title,
          variant: variant.title,
          price: variant.price.amount,
          quantity: 1,
          url: `${process.env.canonicalUrl}${this.$route.path}`,
          content_name: document.title,
          content_category: 'amazon product page',
          content_ids: [product.id],
          content_id: product.id,
          content_type: 'product',
          currency: 'USD'
        })
      } catch (e) {
        console.log("Couldn't send 'Add To Cart' event.")
      }
    },
    trackAdded(added) {
      this.$gtm.push({
        event: 'Add To Cart',
        google_client_id: this.googleClientId,
        cart_id: added.checkout,
        product_id: added.id,
        product_id_shopify: added.decodedId,
        sku: added.sku,
        name: added.name,
        variant: added.variant,
        price: added.price.amount,
        quantity: added.quantity,
        url: added.url
      })
      // klaviyo
      var klaviyo = window.klaviyo || [] // eslint-disable-line
      klaviyo.push([
        'track',
        'Added to Cart',
        {
          Brand: 'Recess',
          Categories: [added.category],
          CompareAtPrice: added.discount,
          ImageURL: added.image,
          Name: added.name,
          Price: added.price.amount,
          ProductID: added.id,
          URL: added.url
        }
      ])
    },
    setSelectedSellingPlan(type, sellingPlanId) {
      const purchaseOption = this.purchaseOptions.find(o => o.type === type)
      this.selected = {
        ...this.selected,
        [type]: {
          ...this.selected[type],
          sellingPlan: purchaseOption.sellingPlans.find(
            o => o.value === sellingPlanId
          )
        }
      }
    },
    setSelectedVariantForOption(type, option) {
      if (type !== this.selectedPurchaseOptionType) {
        return
      }
      const purchaseOption = this.purchaseOptions.find(o => o.type === type)
      const currentVariant = this.selected[type]?.variant
      const selectedOptions = currentVariant.selectedOptions.map(o =>
        o.name === option.name ? option : o
      )
      const selectedVariant = purchaseOption.variants.find(variant =>
        selectedOptions.every(option =>
          variant.selectedOptions.some(
            ({ name, value }) => name === option.name && value === option.value
          )
        )
      )
      if (selectedVariant) {
        this.selected = {
          ...this.selected,
          [type]: {
            ...this.selected[type],
            variant: selectedVariant
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.add-to-cart {
  --size-font-lg: 1rem;
  --size-font-md: 0.875rem;

  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  inline-size: 34rem;
  margin-inline: auto;
  max-inline-size: 100%;
}

@screen md {
  .add-to-cart {
    --size-font-lg: 1.125rem;
    --size-font-md: 1rem;
  }
}

@screen lg {
  .add-to-cart {
    --size-font-lg: 1.25rem;
    --size-font-md: 1.125rem;

    margin-inline-start: 0;
  }
}

form {
  column-gap: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  inline-size: 100%;
  justify-content: center;
  row-gap: 1rem;
}

.purchase-options {
  display: flex;
  flex-flow: column nowrap;
  gap: 1rem;
  inline-size: 100%;
  position: relative;
}

.purchase-options:not(:has(+ .purchase-details)) {
  margin-bottom: 2.25rem;
}

.purchase-option {
  background-color: var(--background-color);
  border: 2px solid currentColor;
  border-radius: 2.5rem;
  display: grid;
  font-size: var(--size-font-md);
  gap: 1em clamp(0.55rem, 2vw, 1.5rem);
  grid-template-columns: min-content minmax(0, 2fr) minmax(0, 1fr);
  line-height: 1.3;
  padding: 1.5rem;
  position: relative;
}

.purchase-option:has(.purchase-option__radio-input:focus-visible) {
  outline: 3px solid var(--input-text-color);
  outline-offset: 4px;
}

.purchase-option:has(.purchase-option__badge) {
  padding-block-start: 3.25rem;
}

.purchase-option > * {
  grid-column: 1 / -1;
}

.purchase-option__price {
  align-self: baseline;
  display: flex;
  flex-wrap: wrap;
  font-size: var(--size-font-lg);
  -webkit-font-smoothing: antialiased;
  font-weight: 500;
  gap: clamp(0.15em, 0.5vw, 0.35em);
  grid-column: 3;
  justify-content: end;
  justify-self: end;
  line-height: 1.1;
  padding-inline-start: 0.25em;
}

.purchase-option__price s {
  font-weight: 400;
}

.purchase-option__price strong {
  font-weight: 700;
}

.purchase-option--selected {
  z-index: 3;
}

.purchase-option__badge {
  background-color: var(--text-color);
  box-shadow: 0.35rem 0.35rem var(--background-color);
  color: var(--background-color);
  color: color-mix(in sRGB, var(--background-color) 70%, #fff 30%);
  font-size: 0.875rem;
  font-weight: 500;
  inset-block-start: 0;
  inset-inline-start: 0;
  padding: 0.375rem 1rem;
  position: absolute;
  text-transform: uppercase;
}

.purchase-option__radio {
  align-self: start;
  display: flex;
  flex-flow: column nowrap;
  grid-column: 1;
  justify-content: center;
}

.purchase-option__radio-input {
  position: absolute;
}

.purchase-option__radio-icon {
  block-size: 1.125em;
  display: inline-block;
  inline-size: auto;
  overflow: visible;
}

.purchase-option:not(.purchase-option--selected)
  .purchase-option__radio-icon
  path {
  opacity: 0;
}

.purchase-option__label {
  cursor: pointer;
  font-size: var(--size-font-lg);
  font-weight: 500;
  grid-column: 2;
  line-height: 1.1;
}

.purchase-option__label::after,
.purchase-option__label::before {
  content: '';
  inset: 0;
  position: absolute;
}

.purchase-option__label::after {
  z-index: 1;
}

.purchase-option--selected .purchase-option__label::before {
  background-color: rgb(255 255 255 / 30%);
  border-radius: 2.5rem;
  z-index: -1;
}

.purchase-option__label strong {
  font-weight: 500;
}

.purchase-details,
.purchase-option__details {
  display: flex;
  flex-direction: column;
}

.purchase-details li,
.purchase-option__details li {
  line-height: 1.4;
  list-style-type: '\2190';
  padding-inline-start: 0.75em;
}

.purchase-details li::marker,
.purchase-option__details li::marker {
  font-family: theme('fontFamily.antidote');
}

.purchase-option__details {
  font-size: var(--size-font-md);
  gap: 0.5em;
  line-height: 1.3;
  padding-inline-start: 1em;
}

.purchase-option__details li {
  max-inline-size: 30ch;
  text-wrap: pretty;
}

@screen md {
  .purchase-option {
    grid-template-columns: min-content minmax(0, 1fr) min-content;
  }

  .purchase-option > * {
    grid-column: 2 / -1;
  }

  .purchase-option > .purchase-option__radio {
    grid-column: 1;
  }

  .purchase-option > .purchase-option__label,
  .purchase-option > .purchase-option__details {
    grid-column: 2;
  }

  .purchase-option__price {
    align-items: flex-end;
    flex-flow: column nowrap;
    grid-column: 3;
    grid-row: span 2;
    justify-content: center;
  }
}

.purchase-details {
  align-items: center;
  font-size: var(--size-font-lg);
  gap: 1em;
  inline-size: 100%;
}

@screen lg {
  .purchase-details {
    font-size: var(--size-font-md);
  }
}

.quantity-input {
  --input-height: stretch;
  --input-border-radius: 2.5rem;

  align-items: stretch;
  display: flex;
  flex-grow: 0;
  min-block-size: 3.75rem;
}

.separator {
  align-items: center;
  display: flex;
  text-align: center;
  text-transform: uppercase;
}

.separator::before,
.separator::after {
  background-color: currentColor;
  block-size: 1px;
  content: '';
  flex: 1;
}

.separator::before {
  margin-inline-end: 1em;
}

.separator::after {
  margin-inline-start: 1em;
}

.amazon-cta {
  align-items: start;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  text-align: center;
}

.amazon-cta__image {
  block-size: 1.875rem;
  display: inline-block;
  inline-size: auto;
  vertical-align: middle;
}

.amazon-cta__text {
  font-size: var(--size-font-md);
}
</style>
